import { DateType } from '@/features/common/models';
import { useMainStore } from '@/features/common/stores';
import { type DeparturesSearchRequest } from '@/features/departures/models';
import { usePlaceAutocompleteHistory } from '@/features/places/composables';
import { useContainer } from '@/plugins/inversify';
import { ISearchService, ISearchServiceId } from '@/services';
import { type DepartureDto, type QuayDto, type PlaceAutocompleteModelDto, PlaceType, type StopPlaceDto } from '@/types/webapi';
import { isDefined } from '@vueuse/core';
import { type AxiosError } from 'axios';
import { isEqual } from 'lodash-es';
import { defineStore, storeToRefs } from 'pinia';
import { computed, reactive, ref, toRefs, watch } from 'vue';
import { useGtm } from 'vue-gtm';
import { useBookingStore } from '@/features/bookings/stores/bookingStore';
import { updateDepartureBookingNotifications } from '@/features/bookings/composables';
import {
    adjustExpectedTimesBasedOnAimed,
    groupByLine,
    groupByQuay,
    sortDepartureGroups,
    updateDeparturesTimeStatusFlags
} from '../helpers';
import { isRealtime } from '@/features/common/helpers';
import { usePlacesStore } from '@/features/places/stores';
import { useShapesStore } from '@/features/map/stores';

export interface State {
    searchRequest: DeparturesSearchRequest;
    selectedDepartureId?: string | null;
    selectedDepartureIdIsExpanded?: boolean;
}

const defaultSearchRequest: DeparturesSearchRequest = {
    dateTime: undefined,
    dateTimeType: DateType.Now,
    filters: {
        modes: [],
        quays: []
    }
};

const defaultSearchResult = {
    errors: [] as string[],
    stopPlace: null as StopPlaceDto | null
};

export const useDeparturesSearchStore = defineStore('departuresSearchStore', () => {
    const container = useContainer();
    const searchService = container.get<ISearchService>(ISearchServiceId);
    const { addItem } = usePlaceAutocompleteHistory<PlaceAutocompleteModelDto>('pastQuays');
    const mainStore = useMainStore();
    const { devModeConfiguration } = storeToRefs(mainStore);
    const gtm = useGtm();
    const ready = ref(false);
    const bookingStore = useBookingStore();
    const placesStore = usePlacesStore();
    const { fromPlace } = storeToRefs(placesStore);
    const shapesStore = useShapesStore();

    const state = reactive({
        isDirty: false,
        isSearching: false,
        isLoading: false,
        searchRequest: { ...defaultSearchRequest },
        selectedDepartureId: null as string | null,
        selectedDepartureIdIsExpanded: false
    });
    const searchResult = reactive({ ...defaultSearchResult });
    const canSearch = computed(() => Boolean(fromPlace.value?.id));
    const hasResult = computed(() => Boolean(searchResult.stopPlace));
    const stopPlaceTransportModes = computed(() => [...(searchResult.stopPlace?.availableTransportModes ?? [])]);
    const stopPlaceQuays = computed<QuayDto[]>(
        () =>
            searchResult.stopPlace?.quays
                // remove duplicates, if any
                .filter((_, i, a) => a.findIndex(x => x.id === a[i].id) === i)
                // sort alphanumerically
                .toSorted((a, b) => a.publicCode?.localeCompare(b.publicCode || '', 'en', { numeric: true }) || 0) || []
    );
    const selectedDeparture = computed<DepartureDto | undefined>(() => {
        if (!searchResult) return undefined;

        for (const key in departureGroups.value) {
            const found = departureGroups.value[key].find(x => x.id === state.selectedDepartureId);
            if (found) {
                return found;
            }
        }

        return undefined;
    });

    const departureGroups = computed((): Record<string, DepartureDto[]> =>
        updateDepartureGroupsRealtime(
            updateDepartureBookingNotifications(
                updateDeparturesTimeStatusFlags(
                    sortDepartureGroups(
                        adjustExpectedTimesBasedOnAimed(removeDuplicateDepartures(searchResult.stopPlace?.departureGroups ?? {}))
                    ),
                    mainStore.currentTime
                ),
                bookingStore.bookings,
                bookingStore.stopOrders
            ),
            mainStore.currentTime
        )
    );
    const departuresGroupedByQuay = computed(() => groupByQuay(departureGroups.value));
    const departuresGroupedByQuayAndLine = computed(() => groupByLine(departuresGroupedByQuay.value));
    const departures = computed(() => Object.values(departureGroups.value).flatMap(x => x));

    function $reset() {
        resetResult();
        state.isDirty = false;
        state.searchRequest = { ...defaultSearchRequest };
        state.selectedDepartureId = null;
        state.selectedDepartureIdIsExpanded = false;
    }

    function resetFilters() {
        state.searchRequest.filters = {
            modes: [],
            quays: []
        };
    }

    function resetResult() {
        Object.assign(searchResult, { ...defaultSearchResult });
        shapesStore.$reset();
    }

    function isValidSearchRequest(): boolean {
        return isDefined(fromPlace.value?.id) && fromPlace.value?.type !== PlaceType.Unknown;
    }

    let searchAbortController: AbortController | undefined;

    async function _executeSearch(searchFunc: Function, onError?: Function) {
        try {
            await searchFunc();
        } catch (e) {
            const { name } = e as AxiosError;
            // in case of AbortController being triggered, do nothing
            if (name === 'CanceledError') return;

            mainStore.registerError(e);
            onError?.();
        }
    }

    async function search() {
        // search for departures for selected quay and time
        if (!isValidSearchRequest()) return;

        resetResult();
        state.isDirty = true;
        state.isLoading = true;

        // register history
        addItem(fromPlace.value!);

        // abort previous request
        searchAbortController?.abort();
        searchAbortController = new AbortController();

        await _executeSearch(
            async () => {
                state.isSearching = true;
                // search for trips with selected criteria
                const result = await searchService.searchForDepartures(
                    { ...state.searchRequest, fromPlace: fromPlace.value },
                    { signal: searchAbortController?.signal }
                );

                // TODO: check for bookings against booking store

                if (result) {
                    searchResult.stopPlace = result;
                    const { shapes = {} } = result || {};

                    // register trip shapes
                    shapesStore.addShapes(shapes);
                }

                state.isSearching = false;
                state.isLoading = false;

                // track event
                gtm?.trackEvent({
                    event: 'app_event',
                    category: 'departures_search',
                    action: 'search',
                    hasDateTime: !!state.searchRequest.dateTime
                });
            },
            () => {
                state.isSearching = false;
                state.isLoading = false;
            }
        );
    }

    function cancelSearch() {
        searchAbortController?.abort();
    }

    // TODO: move this out of here? Maybe it should be handled by the component
    watch(
        selectedDeparture,
        (value, oldValue) => {
            if (value === oldValue) return;

            // if selectedDeparture belongs to the same group, do not reset selectedDepartureIdIsExpanded
            if (
                value &&
                oldValue &&
                value.quay.id === oldValue.quay.id &&
                value.line?.id === oldValue.line?.id &&
                value.tripHeadsign === oldValue.tripHeadsign
            ) {
                return;
            }

            // reset expanded item
            state.selectedDepartureIdIsExpanded = false;
        },
        { deep: true }
    );

    // TODO: should we just reset the modes filter?
    watch(
        () => searchResult.stopPlace,
        value => {
            if (!value) return;

            const { availableTransportModes } = value;
            // if any selected modes are missing in availableTransportModes, remove missing items
            if (state.searchRequest.filters.modes.every(m => availableTransportModes.includes(m))) return;

            state.searchRequest.filters.modes = state.searchRequest.filters.modes.filter(m => availableTransportModes.includes(m));
        },
        { deep: true }
    );

    watch(hasResult, value => {
        // we only want to check against a valid result
        if (!value || !departures.value.length) {
            state.selectedDepartureId = null;
            state.selectedDepartureIdIsExpanded = false;
            return;
        }

        // check if selectedTripPatternId exists in list
        // if it doesn't, reset selected trip pattern stuff
        if (!departures.value.some(x => x.id === state.selectedDepartureId)) {
            // TODO: show error for invalid trip pattern ID?
            state.selectedDepartureId = null;
            state.selectedDepartureIdIsExpanded = false;
        }
    });

    // trigger search when enturEndpointType changes
    watch(devModeConfiguration, async (value, oldValue) => {
        if (isEqual(value, oldValue)) return;

        await search();
    });

    return {
        ...toRefs(state),
        ready,
        searchResult,
        canSearch,
        hasResult,
        stopPlaceTransportModes,
        stopPlaceQuays,
        selectedDeparture,
        departureGroups,
        departuresGroupedByQuay,
        departuresGroupedByQuayAndLine,
        $reset,
        resetFilters,
        resetResult,
        search,
        cancelSearch
    };
});

/**
 * Remove duplicate departures
 * @param departureGroups - The departure groups to remove duplicate departures from
 * @returns The departure groups with duplicate departures removed
 */
function removeDuplicateDepartures(departureGroups: Record<string, DepartureDto[]>): Record<string, DepartureDto[]> {
    return Object.fromEntries(
        Object.entries(departureGroups).map(([key, departures]) => [
            key,
            departures.filter((departure, index, self) => index === self.findIndex(d => d.id === departure.id))
        ])
    );
}

/**
 * Update departure groups realtime values
 * @param departureGroups - The departure groups to update
 * @param currentTime - The current time
 * @returns The departure groups with realtime values
 */
function updateDepartureGroupsRealtime(departureGroups: Record<string, DepartureDto[]>, currentTime: Date): Record<string, DepartureDto[]> {
    return Object.fromEntries(
        Object.entries(departureGroups).map(([key, departures]) => [
            key,
            departures.map(departure => ({
                ...departure,
                realtime: isRealtime(
                    departure.realtime ?? false,
                    currentTime,
                    departure.aimedArrivalTime,
                    departure.aimedDepartureTime,
                    departure.expectedArrivalTime,
                    departure.expectedDepartureTime
                )
            }))
        ])
    );
}
